/**
 * SoftphoneStatusIndicator — phone icon + green/red dot in the header.
 *
 * Per issue #1193: green only when both the apex-voice WebSocket AND the
 * Twilio Device are live. Anything else is red and exposes a Reconnect
 * button. The click is the user gesture that lets the Twilio Device's
 * AudioContext start, which keeps us inside the browser autoplay policy
 * — never auto-trigger reconnect from a timer or event handler.
 *
 * Status comes from `apex:softphone:status` window CustomEvents dispatched
 * by client/lib/softphone.ts. Initial snapshot read on mount.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Phone, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

// Degradation toast (#1061): how long the softphone must stay not-ready
// before we bother the rep. Sub-4s flaps (token rotation, WS blip that
// auto-recovers, the recovery loop's first immediate retry) never toast.
const DEGRADE_TOAST_DELAY_MS = 4_000;
const DEGRADED_TOAST_ID = 'softphone-degraded';

interface SoftphoneStatus {
  voiceConfigured: boolean;
  wsConnected: boolean;
  wsStale: boolean;
  deviceRegistered: boolean;
  ready: boolean;
}

interface SoftphoneWindowApi {
  getSoftphoneStatus?: () => SoftphoneStatus;
  reconnectSoftphone?: () => Promise<void>;
}

const DEFAULT_STATUS: SoftphoneStatus = {
  voiceConfigured: false,
  wsConnected: false,
  wsStale: false,
  deviceRegistered: false,
  ready: false,
};

function getSoftphoneApi(): SoftphoneWindowApi | null {
  return (window as unknown as Record<string, unknown>)[
    'ApexSoftphone'
  ] as SoftphoneWindowApi | null;
}

function readStatus(): SoftphoneStatus {
  return getSoftphoneApi()?.getSoftphoneStatus?.() ?? DEFAULT_STATUS;
}

function statusTitle(s: SoftphoneStatus): string {
  if (s.ready) return 'Softphone ready';
  if (s.wsStale) return 'Softphone disconnected — click Reconnect';
  if (!s.wsConnected) return 'Voice connection down — reconnecting…';
  if (!s.deviceRegistered)
    return 'Phone not registered — reconnecting automatically. Click Reconnect to retry now.';
  return 'Softphone offline';
}

export function SoftphoneStatusIndicator() {
  const [status, setStatus] = useState<SoftphoneStatus>(readStatus);
  const [reconnecting, setReconnecting] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<SoftphoneStatus>).detail;
      if (detail) setStatus(detail);
    };
    window.addEventListener('apex:softphone:status', handler);
    // Late-mount recovery: softphone init may have dispatched before this
    // component mounted. Poll once shortly after to pick up the snapshot
    // we missed.
    const t = setTimeout(() => setStatus(readStatus()), 250);
    return () => {
      window.removeEventListener('apex:softphone:status', handler);
      clearTimeout(t);
    };
  }, []);

  const handleReconnect = useCallback(async () => {
    if (reconnecting) return;
    const api = getSoftphoneApi();
    if (!api?.reconnectSoftphone) return;
    setReconnecting(true);
    try {
      await api.reconnectSoftphone();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Reconnect failed');
    } finally {
      setReconnecting(false);
    }
  }, [reconnecting]);

  // Degradation visibility (#1061): one toast per drop to not-ready, with a
  // recovery toast when the phone comes back. Gated on the softphone having
  // been ready at least once this session — page load starts not-ready by
  // design (Device init waits for a user gesture), and non-leader tabs never
  // become ready at all; neither should nag.
  const wasReadyRef = useRef(false);
  const degradedToastShownRef = useRef(false);
  const degradeTimerRef = useRef<number | null>(null);
  const handleReconnectRef = useRef(handleReconnect);
  handleReconnectRef.current = handleReconnect;

  useEffect(() => {
    if (!status.voiceConfigured) return;
    if (status.ready) {
      wasReadyRef.current = true;
      if (degradeTimerRef.current !== null) {
        window.clearTimeout(degradeTimerRef.current);
        degradeTimerRef.current = null;
      }
      if (degradedToastShownRef.current) {
        degradedToastShownRef.current = false;
        toast.dismiss(DEGRADED_TOAST_ID);
        toast.success('Phone connection restored');
      }
      return;
    }
    if (!wasReadyRef.current) return;
    if (degradedToastShownRef.current || degradeTimerRef.current !== null) return;
    degradeTimerRef.current = window.setTimeout(() => {
      degradeTimerRef.current = null;
      degradedToastShownRef.current = true;
      toast.error('Phone connection lost — reconnecting automatically…', {
        id: DEGRADED_TOAST_ID,
        duration: Infinity,
        action: {
          label: 'Reconnect now',
          onClick: () => void handleReconnectRef.current(),
        },
      });
    }, DEGRADE_TOAST_DELAY_MS);
  }, [status]);

  // Unmount cleanup for the pending degrade timer.
  useEffect(
    () => () => {
      if (degradeTimerRef.current !== null) window.clearTimeout(degradeTimerRef.current);
    },
    [],
  );

  // Hide entirely until the softphone has had a chance to configure itself.
  // Avoids a flash of red on initial page load before /api/config/voice-url
  // resolves.
  if (!status.voiceConfigured) return null;

  const ready = status.ready;
  return (
    <div className="flex items-center gap-1" title={statusTitle(status)}>
      <div className="relative w-7 h-7 rounded-full bg-secondary/40 flex items-center justify-center">
        <Phone className="w-3.5 h-3.5 text-muted-foreground stroke-[1.5]" />
        <span
          className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-card ${
            ready ? 'bg-emerald-500' : 'bg-red-500 animate-pulse'
          }`}
        />
      </div>
      {!ready && (
        <button
          onClick={handleReconnect}
          disabled={reconnecting}
          className="text-[0.625rem] font-medium text-muted-foreground hover:text-foreground px-1.5 py-1 rounded-md hover:bg-secondary/50 cursor-pointer disabled:opacity-50 flex items-center gap-1"
          title="Reconnect softphone"
        >
          <RefreshCw className={`w-3 h-3 ${reconnecting ? 'animate-spin' : ''}`} />
          <span>Reconnect</span>
        </button>
      )}
    </div>
  );
}
